import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useConfig } from '../../routes'
import styled from 'styled-components'
import { styleTheme } from '../../style/theme'
import { getPrimaryColor } from '../../style/utils'
import { FiClock } from 'react-icons/fi'
import { parseISO, isSameDay } from 'date-fns'

import {
  FairDetailContainerProps,
  FairDetailProps,
  BridalFairBase,
  BridalSpecialOffersProps,
  FairContentsProps,
  FairTimesProps,
  IndividuallyEditedFairDetail,
} from './FairDetail.types'
import { FairContents } from './FairContents'
import { Slider } from './components/Slider'
import { Reserve } from './components/Reserve'
import { BridalSpecialOffers } from './BridalSpecialOffers'

type ConfigType = {
  primaryColor: string
}

export const FairDetail: React.FC<FairDetailContainerProps> = (props) => {
  const config = useConfig() as ConfigType
  return <StyledPage {...props} primaryColor={config.primaryColor || '#1babbc'} />
}

const Page: React.FC<FairDetailProps> = ({ className, primaryColor }) => {
  const queryParams = new URLSearchParams(window.location.search)
  const fairDateParam = queryParams.get('fair_date')
  const initialFairDate = fairDateParam ? parseISO(fairDateParam) : null
  const [bridalFairBase, setBridalFairBase] = useState<BridalFairBase>({
    name: '',
    descriptionText: '',
    minutes: null,
    mainImageData: {
      url: '',
      oneToOneImage: { url: '' },
      threeToTwoImage: { url: '' },
      fourToThreeImage: { url: '' },
      sixteenToNineImage: { url: '' },
    },
  })
  const [originalBridalFairBase, setOriginalBridalFairBase] = useState<BridalFairBase>({
    name: '',
    descriptionText: '',
    minutes: null,
    mainImageData: {
      url: '',
      oneToOneImage: { url: '' },
      threeToTwoImage: { url: '' },
      fourToThreeImage: { url: '' },
      sixteenToNineImage: { url: '' },
    },
  })
  const [individuallyEditedFairDetail, setIndividuallyEditedFairDetail] = useState<IndividuallyEditedFairDetail[]>([])
  const [hashTags, setHashTags] = useState([])
  const [bridalSpecialOffers, setBridalSpecialOffers] = useState<BridalSpecialOffersProps>([])
  const [fairContents, setFairContents] = useState<FairContentsProps>([])
  const [fairTimes, setFairTimes] = useState<FairTimesProps>([])
  const { bridalFairDetailKey } = useParams()
  const [loading, setLoading] = useState(true)

  const setBridalFairBaseByDate = useCallback(
    (date: Date) => {
      const editedFair = individuallyEditedFairDetail.find((detail) => isSameDay(detail.particularDate, date))
      if (!editedFair) {
        setBridalFairBase((prev) => originalBridalFairBase)
        return
      }

      setBridalFairBase({
        ...originalBridalFairBase,
        ...editedFair,
        mainImageData: { url: editedFair.imageUrl },
        isEdited: true,
      })
    },
    [individuallyEditedFairDetail, originalBridalFairBase],
  )

  const refresh = () => {
    ;(async () => {
      const url = `/customer/api/v1/bridal_fair_details/${bridalFairDetailKey}`
      const res = await fetch(url)
      const json = await res.json()
      const tags = json.bridalFairTaggings.map((tag) => tag.name)
      if (initialFairDate) {
        const initialEditedFair = json.individuallyEditedFairDetails.find((detail: any) =>
          isSameDay(parseISO(detail.particularDate), initialFairDate),
        )
        const initialFairBase = {
          ...json.bridalFairBase,
          ...initialEditedFair,
          imageData: initialEditedFair ? { image: { url: initialEditedFair.imageUrl } } : json.bridalFairBase.imageData,
          isEdited: !!initialEditedFair,
        }
        setBridalFairBase(initialFairBase)
      } else {
        setBridalFairBase(json.bridalFairBase)
      }
      setOriginalBridalFairBase(json.bridalFairBase)
      setIndividuallyEditedFairDetail(
        json.individuallyEditedFairDetails.map((detail) => ({
          ...detail,
          particularDate: parseISO(detail.particularDate),
        })),
      )
      setHashTags(tags)
      setBridalSpecialOffers(json.bridalSpecialOffersrBases)
      setFairContents(json.fairContents)
      setFairTimes(json.fairTimes)
      setLoading(false)
    })()
  }

  useEffect(() => {
    refresh()
  }, []) // eslint-disable-line react-app/react-hooks/exhaustive-deps

  if (loading) {
    return null
  }

  function createSliderImage(bridalFairBase: BridalFairBase, fairContents: FairContentsProps) {
    const sliderImages: { src: string; alt: string }[] = []
    if (bridalFairBase.mainImageData) {
      sliderImages.push({
        src: bridalFairBase.mainImageData.threeToTwoImage?.url || bridalFairBase.mainImageData.url,
        alt: bridalFairBase.name,
      })
    }
    fairContents.forEach((content) => {
      if (content.imageData) {
        sliderImages.push({
          src: content.imageData.threeToTwoImage?.url || content.imageData.url,
          alt: content.name,
        })
      }
    })

    return sliderImages.filter((image) => image?.src !== null)
  }

  const images = createSliderImage(bridalFairBase, fairContents)

  return (
    <div className={className}>
      {images.length > 0 && (
        <div className="slider">
          <Slider images={images} primaryColor={primaryColor} />
        </div>
      )}
      <h3>{bridalFairBase.name}</h3>
      {hashTags.length > 0 && (
        <div className="tags">
          {hashTags.map((tag, index) => {
            return (
              <span key={index} className="tag">
                #{tag}
              </span>
            )
          })}
        </div>
      )}
      <p className="required-time">
        <FiClock />
        所要時間 {bridalFairBase.minutes}分
      </p>
      <div className="reserve-wrapper">
        <Reserve
          primaryColor={primaryColor}
          fairTimes={fairTimes}
          minutes={bridalFairBase.minutes}
          initialFairDate={initialFairDate}
          pricing={bridalFairBase.pricing}
          price={bridalFairBase.price}
          numberOfPeople={bridalFairBase.numberOfPeople}
          pricingDescriptionText={bridalFairBase.pricingDescriptionText}
          setBridalFairBaseByDate={setBridalFairBaseByDate}
        />
      </div>

      <div className="fair-wrapper">
        <h4>フェア詳細</h4>
        <pre>{bridalFairBase.descriptionText}</pre>
      </div>
      {bridalSpecialOffers.length > 0 && (
        <div className="fair-wrapper">
          <h4>フェア特典</h4>
          <BridalSpecialOffers primaryColor={primaryColor} bridalSpecialOffers={bridalSpecialOffers} />
        </div>
      )}
      {fairContents.length > 0 && (
        <div className="fair-wrapper">
          <h4>フェアに含まれるコンテンツ</h4>
          <FairContents fairContents={fairContents} />
        </div>
      )}
      <div className="reserve-wrapper">
        <h4>開始日時と予約</h4>
        <Reserve
          primaryColor={primaryColor}
          fairTimes={fairTimes}
          minutes={bridalFairBase.minutes}
          initialFairDate={initialFairDate}
          pricing={bridalFairBase.pricing}
          price={bridalFairBase.price}
          numberOfPeople={bridalFairBase.numberOfPeople}
          pricingDescriptionText={bridalFairBase.pricingDescriptionText}
          setBridalFairBaseByDate={setBridalFairBaseByDate}
        />
      </div>
    </div>
  )
}

const StyledPage = styled(Page)`
  color: ${styleTheme.colors.black};
  max-width: 960px;
  margin: 0 auto;
  background-color: ${styleTheme.colors.white};
  padding: 32px 16px;
  @media (max-width: 768px) {
    padding: 16px 0;
  }

  > h3 {
    color: ${styleTheme.colors.black};
    font-family: Noto Sans JP;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      padding: 0 16px;
      font-size: 18px;
    }
  }

  > .tags,
  > .required-time,
  > .fair-wrapper,
  > .reserve-wrapper {
    @media (max-width: 768px) {
      padding: 0 16px;
    }
  }

  > .fair-wrapper {
    padding-bottom: 36px;
    margin-bottom: 48px;
    border-bottom: 2px solid ${styleTheme.colors.gray.light};

    > h4 {
      color: ${getPrimaryColor};
      font-family: Noto Sans JP;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%;
      margin-bottom: 16px;
    }

    > pre {
      white-space: pre-wrap;
      color: ${styleTheme.colors.black};
      font-family: Noto Sans JP;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 170%;
    }
  }

  > .reserve-wrapper {
    > h4 {
      color: ${getPrimaryColor};
      font-size: 18px;
      font-weight: 700;
      line-height: 1.5;
      margin-bottom: 16px;
    }
    margin-bottom: 48px;
  }

  > .tags {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    margin-bottom: 16px;

    .tag {
      color: ${styleTheme.colors.black};
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      padding: 8px 12px;
      border-radius: 4px;
      background-color: ${styleTheme.colors.gray.light};
    }
  }

  > .slider {
    max-width: 660px;
    margin: 40px auto 40px;
  }

  > .required-time {
    color: ${styleTheme.colors.black};
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.5;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
  }
`
